import { logger } from '@adornis/base/logging.js';
import type { Exam, ExamState } from '../../exam.js';
import { runSplitConfirmationAgent } from '../agents/confirm-in-chat-agent.js';
import { confirmSplitForExam } from '../confirm-submission-split.js';
import { isStructureConfirmed, structureExtractionStarted } from './file-assignment.js';

export const confirmSplitInChatToolDef = [
  {
    type: 'function',
    function: {
      name: 'confirm_split_in_chat',
      description:
        'Rufe dieses Tool sofort und automatisch auf, wenn du den User gefragt hast, ob die Anzahl der Schüler und die Aufteilung der Seiten korrekt ist, und der User diese im Chat bestätigt. Rufe das Tool also immer auf nachdem du gefragt hast: "Ich habe 3 Schüler gefunden. Bitte bestätige die Zuweisung vor dem Start der Extraktion." und der User bestätigt.',
      parameters: {
        type: 'object',
        properties: {
          confirmed: { type: 'boolean', description: 'Ob die Zuweisung bestätigt wurde' },
          examId: { type: 'string', description: 'Exam ID' },
          conversationId: { type: 'string', description: 'Conversation ID' },
        },
        required: ['confirmed', 'examId', 'conversationId'],
      },
    },
  },
];

export async function handleConfirmSplitInChat(exam: Exam, toolCallArgs: any): Promise<object> {
  let startStructureExtraction = false;
  let toolResultContent = '';
  toolResultContent = await runSplitConfirmationAgent(Boolean(toolCallArgs.confirmed), exam._id, exam.conversationId);
  if (toolCallArgs.confirmed) {
    const currentExamState: ExamState = exam.examState ?? ({} as any);

    if (!structureExtractionStarted(currentExamState)) {
      // Nothing has read a structure yet → these submissions are the source, and confirming the split
      // is what releases the extraction.
      startStructureExtraction = true;
    } else if (isStructureConfirmed(currentExamState)) {
      logger.info({ examId: exam._id }, 'Structure already confirmed — extracting the new submissions');
      // The shared confirmation side-effect, not a bare extraction call: it marks the new submissions
      // `splitConfirmed` AND queues their extraction. This path used to do only the second half, which was
      // invisible until `answerExtraction` started gating on the flag — see `confirmSplitForExam`.
      void confirmSplitForExam(exam._id).catch(err => {
        logger.error({ err, examId: exam._id }, 'Failed to confirm the split and queue answer extraction');
      });
      const willGrade = !!currentExamState?.hasGradingStarted;
      toolResultContent =
        'Die bestätigte Aufteilung wurde übernommen. Die Schülerabgaben werden jetzt im Hintergrund verarbeitet.' +
        (willGrade ? ' Diese Abgaben werden nach der Verarbeitung automatisch bewertet.' : '');
    } else {
      // A structure is being extracted (or waits for the teacher) — typically from a blank exam that
      // came with the same upload. The answers must not be extracted against tasks the teacher may
      // still edit, and the structure confirmation runs them anyway, so nothing is started here.
      logger.info(
        { examId: exam._id },
        'Structure extraction not confirmed yet — answers deferred to the structure confirmation',
      );
      toolResultContent =
        'Die bestätigte Aufteilung wurde übernommen. Die Aufgabenstruktur dieser Klausur wird gerade erkannt — ' +
        'sag dem Nutzer, dass die Schülerabgaben automatisch verarbeitet werden, sobald er die erkannte ' +
        'Aufgabenstruktur bestätigt hat.';
    }
  }
  return { content: toolResultContent, startStructureExtraction };
}

export function confirmSplitInChatAvailability(_examState: ExamState): boolean {
  return true;
}
